import * as Board from "@escapio/portal/components/board";
import * as Classification from "@escapio/portal/components/classification";
import { RequestPool } from "@escapio/portal/components/hotel-list/RequestPool";
import { getUnavailableState } from "@escapio/portal/state/DirectLink";
import { getFilter } from "@escapio/portal/state/hotel-list/get-category-fitlers";
import { createStore } from "@escapio/shared/data-store/DataStore";
import { data } from "@escapio/shared/state/payload";
import {
	format as formatHash,
	parse as parseHash,
	pushState,
} from "@escapio/shared/state/urlHash";
import { arraysSame, chunk } from "@escapio/shared/util/array";
import { equals, not } from "@escapio/shared/util/function";
import { fetchPricesForCurrentTravelData } from "../../api/hotel-prices";
import { contains as favoritesContain } from "../../state/FavoriteHotels";
import { addFilter, removeFilter } from "../../state/UsedFilters";
import { travelData } from "../../state/travelData";
import { ESC, NORMAL, TOP } from "../HotelHighlightStates";
import {
	STATE_AVAILABLE,
	STATE_DIRECT_LINK_ONLY,
	STATE_LOADING,
	STATE_WITHOUT_DATES,
} from "@escapio/portal/state/HotelPriceLoadingStates";
import { getHistogramClass } from "./PriceFilterMaps";
import { BY_POPULARITY, BY_PRICE_AND_POPULARITY, sorters } from "./Sorting";
import { DEAL_PARAM } from "@escapio/portal/components/deals/deal-param";
import { getDiscount } from "@escapio/portal/components/get-discount.js";
import { PROVIDER_ESCAPIO } from "@escapio/portal/api/provider-codes";

const { hotel_list: hotels } = data;
const ITEMS_PER_PAGE = 20;

const CHUNK_SIZES = [5, 20, 300];
const MAX_REQUESTS = 5;

const DEFAULT_SETTINGS = Object.freeze({
	sorting: BY_POPULARITY,
	page: 1,
	topHotelId: null,
	preselectedDealId: null,
	filters: Object.freeze({
		criteria: Object.freeze([]),
		hotelTypes: Object.freeze([]),
		price: null,
		contract: false, // Used in admin-tools.
		discount: false, // Used in admin-tools.
		board: Board.NONE,
		cancelation: false,
		reviewScore: null,
		classification: Classification.ALL,
	}),
});

const PRICE_CHANGING_FILTERS = ["board", "cancelation"];

// ID for price request rounds, used to ignore deprecated update price actions
let latestRequestPricesRound = 0;
const pool = new RequestPool(MAX_REQUESTS);

const getBestPrice = pricesForHotel => pricesForHotel.offers[0].total_rate;

const filterMatcher = (filters, prices, priceFilterImmuneIds) => {
	// Set defaults values for missing filter settings
	filters = { ...DEFAULT_SETTINGS.filters, ...filters };

	// List of test functions build from active filters
	const tester = [];

	["criteria", "hotelTypes"].forEach(multiToggleFilter => {
		if (filters[multiToggleFilter].length > 0) {
			tester.push(hotel =>
				filters[multiToggleFilter].every(filterId =>
					hotel[multiToggleFilter].includes(filterId),
				),
			);
		}
	});

	if (filters.price !== null) {
		tester.push(
			({ id }) =>
				priceFilterImmuneIds.indexOf(id) !== -1 ||
				(prices.hasOwnProperty(id) &&
					(prices[id].pricesState === STATE_LOADING ||
						(prices[id].pricesState === STATE_AVAILABLE &&
							getBestPrice(prices[id]) <= filters.price))),
		);
	}

	if (filters.reviewScore !== null) {
		tester.push(hotel => hotel["review_score"] >= filters.reviewScore);
	}

	if (filters.classification !== Classification.ALL) {
		tester.push(hotel =>
			Classification.isAtLeast(filters.classification, hotel.classification),
		);
	}

	if (filters.contract) {
		tester.push(({ hasContract }) => hasContract);
	}

	if (filters.discount) {
		tester.push(
			({ id }) =>
				prices[id] === undefined ||
				Boolean(getDiscount(prices[id]?.offers || [])),
		);
	}

	return hotel => tester.every(tester => tester(hotel));
};

/** Given a hotel, returns true if it's "in the near", otherwise false. */
const inTheNear = ({ distance }) => distance !== -1;

const filter = (list, filters, state) => {
	const THRESHOLD = 5;
	const matcher = filterMatcher(
		filters,
		state.prices,
		getPriceFilterImmuneIds(state),
	);

	// If we have more than 5 available or loading hotels, filters included,
	// filter out hotels in the near.
	const hotelsInPlace = hotels.filter(not(inTheNear)).filter(matcher);
	const unavailableInPlace = (state.hotelsUnavailable || [])
		.filter(not(inTheNear))
		.filter(matcher);

	if (
		hotelsInPlace.length - unavailableInPlace.length > THRESHOLD ||
		!travelData.hasDates
	) {
		list = list.filter(not(inTheNear));
	}

	return list.filter(matcher);
};

const sort = (state, hotels) => {
	if (!sorters.hasOwnProperty(state.sorting)) {
		throw new Error(`Invalid sorting value: ${state.sorting}`);
	}

	return hotels.sort((a, b) => {
		if (inTheNear(a) !== inTheNear(b)) {
			// no distance hotels first
			return !inTheNear(a) ? -1 : 1;
		}

		if (a.id === state.topHotelId) {
			return -1;
		}

		if (b.id === state.topHotelId) {
			return 1;
		}

		const isSortingWithPrice = state.sorting == BY_PRICE_AND_POPULARITY;
		const belongsPriceGroup = hotel =>
			hasPrice(state.prices, hotel.id) ||
			(!isSortingWithPrice && hotel.disabled_prices);

		if (belongsPriceGroup(a) && !belongsPriceGroup(b)) {
			return -1;
		}
		if (!belongsPriceGroup(a) && belongsPriceGroup(b)) {
			return 1;
		}

		if (state.sorting == BY_POPULARITY) {
			if (favoritesContain(a.id) && !favoritesContain(b.id)) {
				return -1;
			}

			if (!favoritesContain(a.id) && favoritesContain(b.id)) {
				return 1;
			}
		}

		return sorters[state.sorting](state.prices)(a, b);
	});
};

/**
 * Remove hotels from a list if in given second list.
 * Assumes that hotels are ONLY contained ONCE.
 *
 * @param {array} hotels
 * @param {array} unwantedHotels
 */
const removeHotels = (hotels, unwantedHotels) => {
	return hotels.filter(hotel => {
		let foundIndex;
		if (
			unwantedHotels.some((unwantedHotel, index) => {
				if (unwantedHotel.id === hotel.id) {
					foundIndex = index;
					return true;
				}
				return false;
			})
		) {
			// Reduce unwanted list.
			unwantedHotels = (
				foundIndex > 0 ? unwantedHotels.slice(0, foundIndex) : []
			).concat(
				foundIndex < unwantedHotels.length - 1
					? unwantedHotels.slice(foundIndex + 1)
					: [],
			);
			return false;
		}
		return true;
	});
};

const getPriceFilterImmuneIds = state => {
	const ids = [];

	if (state.topHotelId) {
		ids.push(state.topHotelId);
	}

	return ids;
};

const getHotel = id => hotels.find(h => h.id === id);

const hasPrice = (prices, id) =>
	prices.hasOwnProperty(id) &&
	typeof prices[id] === "object" &&
	prices[id].offers;

/*******************************
 * Non-filter settings actions *
 *******************************/

export const ACTION_SET_PAGE = (
	state,
	page = DEFAULT_SETTINGS.page,
	dispatch,
) => {
	if (page !== state.page) {
		document.dispatchEvent(new CustomEvent("hotelListPaginated"));
	}
	return ACTION_UPDATE_HASH({ ...state, page }, undefined, dispatch);
};

export const ACTION_SET_SORTING = (state, sorting, dispatch) => {
	if (!sorters.hasOwnProperty(sorting)) {
		sorting = DEFAULT_SETTINGS.sorting;
	}

	return ACTION_SET_PAGE(
		removeTopHotel({ ...state, sorting }),
		undefined,
		dispatch,
	);
};

const removeTopHotel = state => {
	if (!state.topHotelId) {
		return state;
	}

	// Check if former top hotel must be moved to unavailable list.
	const topHotel = getHotel(state.topHotelId);
	const mustBeMovedToUnavailable =
		state.prices[state.topHotelId] &&
		state.prices[state.topHotelId].pricesState != STATE_LOADING &&
		state.prices[state.topHotelId].pricesState != STATE_AVAILABLE;
	if (!topHotel || !mustBeMovedToUnavailable) {
		return { ...state, topHotelId: DEFAULT_SETTINGS.topHotelId };
	}

	const hotelsAvailable = state.hotelsAvailable.filter(({ id }) => {
		return id != state.topHotelId;
	});
	const hotelsUnavailable = state.hotelsUnavailable;
	hotelsUnavailable.push(topHotel);

	return {
		...state,
		hotelsAvailable,
		hotelsUnavailable,
		topHotelId: DEFAULT_SETTINGS.topHotelId,
	};
};

/*****************
 * Price actions *
 *****************/

const ACTION_RESET_PRICES = (state, _, dispatch) => {
	latestRequestPricesRound++;

	pool.abort();

	const hotelsNotLoaded = [];
	const hotelsUnavailable = [];
	const prices = {};
	hotels.forEach(hotel => {
		if (hotel.disabled_prices) {
			hotelsUnavailable.push(hotel);
			prices[hotel.id] = {
				pricesState: getUnavailableState(hotel),
			};
		} else {
			hotelsNotLoaded.push(hotel);
		}
	});

	return ACTION_CHECK_FOR_MISSING_PRICES(
		{
			...state,
			hotelsNotLoaded,
			hotelsLoading: [],
			hotelsAvailable: [],
			hotelsUnavailable,
			prices,
			lastPricesResetTime: Date.now(),
			lastPricesResetDuration: null,
		},
		undefined,
		dispatch,
	);
};

const ACTION_CHECK_FOR_MISSING_PRICES = (state, _, dispatch) => {
	if (!travelData.hasDates) {
		return state;
	}

	const preSorting =
		state.sorting === BY_PRICE_AND_POPULARITY ? BY_POPULARITY : state.sorting;
	let hotelsForRequest = sort(
		{ ...state, sorting: preSorting },
		filter(
			state.hotelsNotLoaded,
			{ ...state.filters, price: DEFAULT_SETTINGS.filters.price },
			state,
		),
	);

	const prices = {};
	hotelsForRequest.forEach(hotel => {
		prices[hotel.id] = {
			pricesState: STATE_LOADING,
		};
	});

	const requestPricesRound = latestRequestPricesRound;
	// Build chunks of hotels to be loaded.
	chunk(CHUNK_SIZES, hotelsForRequest).forEach(chunk => {
		pool
			.append(() =>
				fetchPricesForCurrentTravelData(
					chunk.map(hotel => hotel.id),
					{
						board: state.filters.board,
						cancelation: state.filters.cancelation,
					},
				),
			)
			.then(prices =>
				dispatch(ACTION_UPDATE_PRICES, {
					chunk,
					prices,
					requestPricesRound,
				}),
			)
			.catch(error => {
				if (error.name == "AbortError") {
					return;
				}
				console.error(error);
				// Mark failed hotels as unavailable.
				dispatch(ACTION_UPDATE_PRICES, {
					chunk,
					prices: {},
					requestPricesRound,
				});
			});
	});

	// Move hotels from notLoaded list to loading list.
	const hotelsLoading = state.hotelsLoading.concat(hotelsForRequest);
	const hotelsNotLoaded = removeHotels(state.hotelsNotLoaded, hotelsForRequest);

	return {
		...state,
		hotelsNotLoaded,
		hotelsLoading,
		prices: { ...state.prices, ...prices },
	};
};

const ACTION_UPDATE_PRICES = (state, { chunk, prices, requestPricesRound }) => {
	if (requestPricesRound != latestRequestPricesRound) {
		// Ignore responses from previous rounds.
		return state;
	}

	const unavailable = [];
	const available = [];
	const mappedPrices = {};
	let priceFilterRange = state.priceFilterRange;

	chunk.forEach(hotel => {
		mappedPrices[hotel.id] = hasPrice(prices, hotel.id)
			? { ...prices[hotel.id] }
			: {};
		let bestPrice = null;
		if (hasPrice(prices, hotel.id)) {
			bestPrice = getBestPrice(prices[hotel.id]);
			if (priceFilterRange) {
				priceFilterRange = [
					Math.min(priceFilterRange[0], bestPrice),
					Math.max(priceFilterRange[1], bestPrice),
				];
			} else {
				priceFilterRange = [bestPrice, bestPrice];
			}
		}

		// Immune hotels are always visible in every price state,
		// but can have "no offers".
		if (getPriceFilterImmuneIds(state).indexOf(hotel.id) !== -1) {
			available.push(hotel);

			if (
				bestPrice &&
				(!state.filters.price || bestPrice < state.filters.price)
			) {
				mappedPrices[hotel.id].pricesState = STATE_AVAILABLE;
			} else {
				mappedPrices[hotel.id].pricesState = getUnavailableState(hotel);
			}
			return;
		}

		// Other hotels will not be shown without price.
		if (bestPrice) {
			available.push(hotel);
			mappedPrices[hotel.id].pricesState = STATE_AVAILABLE;
		} else {
			unavailable.push(hotel);
			mappedPrices[hotel.id].pricesState = getUnavailableState(hotel);
		}
	});

	state = {
		...state,
		hotelsLoading: removeHotels(state.hotelsLoading, chunk),
		hotelsAvailable: state.hotelsAvailable.concat(available),
		hotelsUnavailable: state.hotelsUnavailable.concat(unavailable),
		prices: { ...state.prices, ...mappedPrices },
		priceFilterRange,
	};

	if (pool.isEmpty()) {
		if (state.lastPricesResetDuration) {
			// All hotels have been loaded before.
			// This should not happen here, just to make sure.
			return state;
		}
		// All hotels have been loaded just now.
		// Reset price filter if out of range.
		if (priceFilterRange && state.filters.price) {
			if (
				state.filters.price < priceFilterRange[0] ||
				state.filters.price > priceFilterRange[1]
			) {
				state = ACTION_UPDATE_HASH({
					...state,
					filters: { ...state.filters, price: DEFAULT_SETTINGS.filters.price },
				});
			}
		}
		return {
			...state,
			lastPricesResetDuration: Date.now() - state.lastPricesResetTime,
		};
	}

	return state;
};

export const ACTION_UPDATE_TRAVEL_DATA = (state, payload, dispatch) => {
	return ACTION_SET_PAGE(
		ACTION_RESET_PRICES(
			{
				...state,
				filters: {
					...state.filters,
					discount: payload.hasDates ? state.filters.discount : false,
				},
			},
			undefined,
			dispatch,
		),
		undefined,
		dispatch,
	);
};

/******************
 * Filter actions *
 ******************/

const createFilterHelper =
	(...actions) =>
	callback =>
	(state, payload, dispatch) => {
		return actions.reduce(
			(state, action) => action(state, undefined, dispatch),
			{
				...state,
				filters: { ...state.filters, ...callback(payload, state.filters) },
			},
		);
	};
const createFilterAction = createFilterHelper(
	ACTION_SET_PAGE,
	ACTION_CHECK_FOR_MISSING_PRICES,
);
const createFilterResetPricesAction = createFilterHelper(
	ACTION_SET_PAGE,
	ACTION_RESET_PRICES,
);

export const ACTION_REMOVE_ALL_FILTER = (state, _, dispatch) => {
	const intermediateState = ACTION_SET_PAGE(
		{ ...state, filters: DEFAULT_SETTINGS.filters },
		undefined,
		dispatch,
	);

	return PRICE_CHANGING_FILTERS.some(name => state.filters[name])
		? ACTION_RESET_PRICES(intermediateState, undefined, dispatch)
		: ACTION_CHECK_FOR_MISSING_PRICES(intermediateState, undefined, dispatch);
};

/**
 * Warning: This filter only works with "criteria" and "hotel type" filters!
 */
export const ACTION_SET_TOGGLE_FILTER = (state, payload, dispatch) => {
	addFilter(payload);
	return createFilterAction(({ name, value }, filters) => ({
		[name]: [...filters[name], value],
	}))(state, payload, dispatch);
};
export const ACTION_REMOVE_TOGGLE_FILTER = (state, payload, dispatch) => {
	removeFilter(payload);
	return createFilterAction(({ name, value }, filters) => ({
		[name]: filters[name].filter(not(equals(value))),
	}))(state, payload, dispatch);
};

const ACTION_CHANGE_PRICE_FILTER = (state, payload, dispatch) => {
	// Update state for price immune hotels, when matching price filter again,
	// or when stopped matching price filter.
	let newPrices = {};
	getPriceFilterImmuneIds(state).forEach(id => {
		if (!state.prices[id].hasOwnProperty("offers")) {
			return;
		}

		const hotel = getHotel(id);
		if (!payload || getBestPrice(state.prices[id]) < payload) {
			newPrices[id] = { ...state.prices[id], pricesState: STATE_AVAILABLE };
		} else {
			newPrices[id] = {
				...state.prices[id],
				pricesState: getUnavailableState(hotel),
			};
		}
	});

	return ACTION_CHECK_FOR_MISSING_PRICES(
		ACTION_SET_PAGE(
			{
				...state,
				filters: { ...state.filters, price: payload },
				prices: { ...state.prices, ...newPrices },
			},
			undefined,
			dispatch,
		),
		undefined,
		dispatch,
	);
};
export const ACTION_SET_PRICE_FILTER = (state, payload, dispatch) => {
	if (
		state.priceFilterRange &&
		(payload < state.priceFilterRange[0] || payload > state.priceFilterRange[1])
	) {
		return state;
	}
	return ACTION_CHANGE_PRICE_FILTER(state, payload, dispatch);
};
export const ACTION_RESET_PRICE_FILTER = (state, _, dispatch) => {
	return ACTION_CHANGE_PRICE_FILTER(
		state,
		DEFAULT_SETTINGS.filters.price,
		dispatch,
	);
};

export const ACTION_TOGGLE_CANCELATION_FILTER = createFilterResetPricesAction(
	cancelation => ({ cancelation: Boolean(cancelation) }),
);
export const ACTION_RESET_CANCELATION_FILTER = createFilterResetPricesAction(
	() => ({ cancelation: DEFAULT_SETTINGS.filters.cancelation }),
);

export const ACTION_CHANGE_BOARD_FILTER = createFilterResetPricesAction(
	board => ({ board: board }),
);
export const ACTION_RESET_BOARD_FILTER = createFilterResetPricesAction(() => ({
	board: DEFAULT_SETTINGS.filters.board,
}));

export const ACTION_SET_CONTRACT_FILTER = createFilterAction(contract => ({
	contract: Boolean(contract),
}));

export const ACTION_SET_DISCOUNT_FILTER = createFilterAction(discount => ({
	discount: Boolean(discount),
}));

export const ACTION_SET_SCORE_FILTER = (
	state,
	payload,
	dispatch,
	{ filteredCount },
) => {
	// Check if new value would create an empty list
	if (!filteredCount({ ...state.filters, reviewScore: payload })) {
		// Force update to have ScoreFilter slider show correct value
		return { ...state };
	}

	return ACTION_CHECK_FOR_MISSING_PRICES(
		ACTION_SET_PAGE(
			{ ...state, filters: { ...state.filters, reviewScore: payload } },
			undefined,
			dispatch,
		),
		undefined,
		dispatch,
	);
};
export const ACTION_RESET_SCORE_FILTER = createFilterAction(() => ({
	reviewScore: DEFAULT_SETTINGS.filters.reviewScore,
}));

export const ACTION_SET_CLASSIFICATION_FILTER = (
	state,
	payload,
	dispatch,
	{ filteredCount },
) => {
	// Check if new value would create an empty list
	if (!filteredCount({ ...state.filters, classification: payload })) {
		// Force an update to have classification slider show correct value
		return { ...state };
	}

	return ACTION_CHECK_FOR_MISSING_PRICES(
		ACTION_SET_PAGE(
			{ ...state, filters: { ...state.filters, classification: payload } },
			undefined,
			dispatch,
		),
		undefined,
		dispatch,
	);
};
export const ACTION_RESET_CLASSIFICATION_FILTER = createFilterAction(() => ({
	classification: DEFAULT_SETTINGS.filters.classification,
}));

/****************
 * Hash actions *
 ****************/

export const ACTION_PARSE_HASH = (state, hash, dispatch) => {
	if (state.hash == hash) {
		return state;
	}

	const parseId = id => parseInt(id, 10);
	const isNaN = x => x !== x;

	const filtersFromHash = parseHash({
		CriteriaFilter: [
			"criteria",
			value =>
				value
					.split(",")
					.map(parseId)
					.filter(not(isNaN))
					.filter(id => getFilter("criteria", id)),
		],
		HotelTypeFilter: [
			"hotelTypes",
			value =>
				value
					.split(",")
					.map(parseId)
					.filter(not(isNaN))
					.filter(id => getFilter("hotelTypes", id)),
		],
		PriceFilter: [
			"price",
			value =>
				!state.priceFilterRange ||
				(parseId(value) >= state.priceFilterRange[0] &&
					parseId(value) <= state.priceFilterRange[1])
					? parseId(value)
					: DEFAULT_SETTINGS.filters.price,
		],
		ContractFilter: ["contract", value => Boolean(value)],
		DiscountFilter: ["discount", value => Boolean(value)],
		Board: [
			"board",
			value => (Board.isValid(value) ? value : DEFAULT_SETTINGS.filters.board),
		],
		Cancelation: ["cancelation", value => Boolean(value)],
		ScoreFilter: [
			"reviewScore",
			value =>
				!isNaN(parseId(value))
					? parseId(value)
					: DEFAULT_SETTINGS.filters.reviewScore,
		],
		Classification: [
			"classification",
			value =>
				Classification.isValid(value)
					? value
					: DEFAULT_SETTINGS.filters.classification,
		],
	})(hash);

	const paramsFromHash = parseHash({
		page: value =>
			!isNaN(parseId(value)) ? parseId(value) : DEFAULT_SETTINGS.page,
		SortFilter: [
			"sorting",
			value =>
				sorters.hasOwnProperty(value) ? value : DEFAULT_SETTINGS.sorting,
		],
		th: [
			"topHotelId",
			value =>
				!isNaN(parseId(value)) ? parseId(value) : DEFAULT_SETTINGS.topHotelId,
		],
		[DEAL_PARAM]: ["preselectedDealId", parseId],
	})(hash);

	const intermediateState = ACTION_UPDATE_HASH(
		{
			...state,
			...DEFAULT_SETTINGS,
			...paramsFromHash,
			filters: { ...DEFAULT_SETTINGS.filters, ...filtersFromHash },
		},
		undefined,
		dispatch,
	);
	return PRICE_CHANGING_FILTERS.some(
		name => state.filters[name] !== filtersFromHash[name],
	)
		? ACTION_RESET_PRICES(intermediateState, undefined, dispatch)
		: ACTION_CHECK_FOR_MISSING_PRICES(intermediateState, undefined, dispatch);
};

const ACTION_UPDATE_HASH = state => {
	const equals = defaultValue => value => value == defaultValue;

	const mapIfNotDefault =
		(test, map = value => value) =>
		value =>
			test(value) ? null : map(value);

	const newHash = formatHash({
		criteria: [
			"CriteriaFilter",
			mapIfNotDefault(
				value => arraysSame(DEFAULT_SETTINGS.filters.criteria, value),
				value => value.join(","),
			),
		],
		hotelTypes: [
			"HotelTypeFilter",
			mapIfNotDefault(
				value => arraysSame(DEFAULT_SETTINGS.filters.hotelTypes, value),
				value => value.join(","),
			),
		],
		contract: [
			"ContractFilter",
			mapIfNotDefault(equals(DEFAULT_SETTINGS.filters.contract), value =>
				value ? 1 : 0,
			),
		],
		discount: [
			"DiscountFilter",
			mapIfNotDefault(equals(DEFAULT_SETTINGS.filters.discount), value =>
				value ? 1 : 0,
			),
		],
		board: ["Board", mapIfNotDefault(equals(DEFAULT_SETTINGS.filters.board))],
		cancelation: [
			"Cancelation",
			mapIfNotDefault(equals(DEFAULT_SETTINGS.filters.cancelation), value =>
				value ? 1 : 0,
			),
		],
		price: [
			"PriceFilter",
			mapIfNotDefault(equals(DEFAULT_SETTINGS.filters.price), Math.floor),
		],
		reviewScore: [
			"ScoreFilter",
			mapIfNotDefault(equals(DEFAULT_SETTINGS.filters.reviewScore)),
		],
		classification: [
			"Classification",
			mapIfNotDefault(equals(DEFAULT_SETTINGS.filters.classification)),
		],
		page: mapIfNotDefault(equals(DEFAULT_SETTINGS.page)),
		sorting: ["SortFilter", mapIfNotDefault(equals(DEFAULT_SETTINGS.sorting))],
		topHotelId: ["th", mapIfNotDefault(equals(DEFAULT_SETTINGS.topHotelId))],
		preselectedDealId: [
			DEAL_PARAM,
			mapIfNotDefault(equals(DEFAULT_SETTINGS.preselectedDealId)),
		],
	})({ ...state, ...state.filters });

	if (state.hash == newHash) {
		return state;
	}

	// Add new entry to browser history.
	pushState(newHash);

	return { ...state, hash: newHash };
};

/********
 * Init *
 ********/
const hotelsAvailableAndPossibleEDLOnly = state =>
	state.hotelsAvailable.concat(
		state.hotelsUnavailable.filter(
			hotel =>
				hotel.disabled_prices ||
				(state.prices[hotel.id].pricesState === STATE_DIRECT_LINK_ONLY &&
					hotel.distance === -1),
		),
	);

const hotelsLoadedFiltered = state => filters =>
	filter(
		travelData.hasDates ? hotelsAvailableAndPossibleEDLOnly(state) : hotels,
		filters,
		state,
	);

const hotelsLoadedFilteredCount = state => filters =>
	hotelsLoadedFiltered(state)(filters).length;

const hotelsVisibleFiltered = state => filters =>
	filter(
		travelData.hasDates
			? hotelsAvailableAndPossibleEDLOnly(state).concat(state.hotelsLoading)
			: hotels,
		filters,
		state,
	);

const fromEntries = entries =>
	entries.reduce(
		(acc, [key, value]) => Object.assign(acc, { [key]: value }),
		{},
	);

let lastCountedState = null;
let lastCounters = null;
const getCounters = state => {
	let countedHotels = hotelsLoadedFiltered(state)(state.filters);

	const scores = [0, 85, 90, 95];
	const counters = {
		unfiltered: countedHotels.length,
		score: fromEntries(scores.map(value => [value, 0])),
		classification: fromEntries(
			Object.keys(Classification.OPTIONS).map(key => [key, 0]),
		),
	};

	const toggles = ["hotelTypes", "criteria"];
	toggles.forEach(name => {
		counters[name] = fromEntries(data[name].map(({ value }) => [value, 0]));
	});

	countedHotels.forEach(hotel => {
		toggles.forEach(name => {
			data[name].forEach(({ value }) => {
				if (hotel[name].includes(value)) {
					counters[name][value]++;
				}
			});
		});
	});

	(state.filters.reviewScore !== null
		? hotelsLoadedFiltered(state)({ ...state.filters, reviewScore: null })
		: countedHotels
	).forEach(hotel => {
		scores.forEach(value => {
			if (hotel.review_score >= value) {
				counters.score[value]++;
			}
		});
	});

	(state.filters.classification !== Classification.ALL
		? hotelsLoadedFiltered(state)({
				...state.filters,
				classification: Classification.ALL,
			})
		: countedHotels
	).forEach(hotel => {
		Object.keys(Classification.OPTIONS).forEach(key => {
			if (Classification.isAtLeast(key, hotel.classification)) {
				counters.classification[key]++;
			}
		});
	});

	return counters;
};

// Store instance with initial values.
export const store = createStore(
	{
		...DEFAULT_SETTINGS,
		allHotels: hotels,
		hotelsNotLoaded: [],
		hotelsLoading: [],
		hotelsAvailable: [],
		hotelsUnavailable: [],
		prices: {},
		lastPricesResetTime: Date.now(),
		lastPricesResetDuration: null,
		priceFilterRange: null,
		hash: undefined,
	},
	{
		// Check that the prices object is not empty to prevent false negatives of
		// "isLoading", which causes false positives for empty list events.
		isLoading: state =>
			Object.keys(state.prices).length === 0 || state.hotelsLoading.length > 0,
		// @TODO Reduce duplicate code and cache results? Count by reduce instead of using array.filter?
		filteredCount: state => {
			return filters => {
				return hotelsLoadedFilteredCount(state)(filters);
			};
		},
		filtersCount: ({ filters }) => {
			let count = ["criteria", "hotelTypes"].reduce(
				(sum, filterName) => sum + filters[filterName].length,
				0,
			);
			count += filters.price !== null ? 1 : 0;
			count += filters.reviewScore !== null ? 1 : 0;
			count += filters.board !== DEFAULT_SETTINGS.filters.board ? 1 : 0;
			count += filters.cancelation ? 1 : 0;
			return count;
		},
		counters: state => {
			if (lastCountedState !== state) {
				lastCountedState = state;
				lastCounters = getCounters(state);
			}
			return lastCounters;
		},
		visiblePagesCount: state =>
			Math.ceil(
				hotelsVisibleFiltered(state)(state.filters).length / ITEMS_PER_PAGE,
			),
		visibleHotels: state => hotelsLoadedFiltered(state)(state.filters),
		visiblePage: state => {
			const start = (state.page - 1) * ITEMS_PER_PAGE,
				end = state.page * ITEMS_PER_PAGE;

			const visibleHotels = hotelsVisibleFiltered(state)(state.filters);
			let page = sort(state, visibleHotels).slice(start, end);

			return page.map((hotel, index) => {
				return {
					...hotel,
					state: {
						...(travelData.hasDates
							? state.prices[hotel.id]
							: { pricesState: STATE_WITHOUT_DATES }),
						type:
							hotel.id === state.topHotelId
								? TOP
								: state?.prices[hotel.id]?.offers?.[0]?.provider ==
									  PROVIDER_ESCAPIO
									? ESC
									: NORMAL,
						position: (state.page - 1) * ITEMS_PER_PAGE + index + 1,
					},
				};
			});
		},
		priceFiltersVisible: ({
			hotelsAvailable,
			hotelsLoading,
			prices,
			filters,
		}) => {
			// If any hotel is loading, price filters should be visible.
			if (hotelsLoading.length > 0) {
				return true;
			}

			// If any rate related filter is set, price filters should be visible.
			if (filters.board !== Board.NONE || filters.cancelation) {
				return true;
			}

			// If list is completely empty and no rate filters set, prices filters should be hidden.
			if (hotelsAvailable.length === 0) {
				return false;
			}

			// If all available hotels are EDL-only (no offers) then price filters
			// should be hidden too.
			return !hotelsAvailable.every(
				hotel => prices[hotel.id].pricesState === STATE_DIRECT_LINK_ONLY,
			);
		},
		priceHistogram: state => {
			const { hotelsAvailable, prices, priceFilterRange } = state;
			return priceFilterRange && priceFilterRange[1] - priceFilterRange[0] > 200
				? hotelsAvailable
						.map(({ id }) => id)
						.filter(id => !getPriceFilterImmuneIds(state).includes(id))
						.reduce((histogram, id) => {
							histogram[
								getHistogramClass(
									20,
									getBestPrice(prices[id]),
									priceFilterRange[0],
									priceFilterRange[1],
								)
							]++;
							return histogram;
						}, new Array(20).fill(0))
				: null;
		},
	},
);
